import { atom } from 'jotai'

import AuthService from '../../services/AuthService'
import ChatService from '../../services/ChatService'
import { authDataAtom } from '../user/AtomUsers'

/**
 * Auth 命令原子集合
 *
 * 提供登录、Google 认证和登出命令，用于更新认证状态：
 *
 * - loginCommandAtom：执行登录操作
 * - authGoogleCommandAtom：执行 Google 认证操作
 * - logoutCommandAtom：执行登出操作
 */

/**
 * 登录命令原子
 *
 * 使用方式：
 * const login = useSetAtom(loginCommandAtom)
 * await login({ email: 'user@example.com', password: 'password',  })
 *
 * @param {Object} payload - 登录参数
 * @param {string} payload.email - 用户邮箱
 * @param {string} payload.password - 用户密码
 * @param {Object} payload.api - (已弃用) API 实例不再需要）
 *
 * 此命令会：
 * - 调用 AuthService.login 进行登录
 * - 更新 authDataAtom 存储认证数据
 * - 如果登录失败，会抛出错误
 */
export const loginCommandAtom = atom(
  null, // read function (not used for commands)
  async (_get, set, payload) => {
    if (!payload || typeof payload !== 'object') {
      console.warn('[loginCommandAtom] requires payload object')
      return
    }

    const { email, password } = payload

    if (!email || !password) {
      console.warn('[loginCommandAtom] requires email and password')
      throw new Error('Email and password are required')
    }

    try {
      // 调用 AuthService 进行登录
      const newAuthData = await AuthService.login(email, password)

      // 更新 authDataAtom
      set(authDataAtom, newAuthData)

      return newAuthData
    } catch (error) {
      console.error('[loginCommandAtom] login failed:', error)
      throw error
    }
  },
)

/**
 * Google 认证命令原子
 *
 * 使用方式：
 * const authGoogle = useSetAtom(authGoogleCommandAtom)
 * await authGoogle({ tokenResponse: googleTokenResponse,  })
 *
 * @param {Object} payload - Google 认证参数
 * @param {Object} payload.tokenResponse - Google OAuth token 响应对象（包含 access_token）
 * @param {Object} payload.api - (已弃用) API 实例不再需要）
 *
 * 此命令会：
 * - 调用 AuthService.authGoogle 进行 Google 认证
 * - 更新 authDataAtom 存储认证数据
 * - 如果认证失败，会抛出错误
 */
export const authGoogleCommandAtom = atom(
  null, // read function (not used for commands)
  async (_get, set, payload) => {
    if (!payload || typeof payload !== 'object') {
      console.warn('[authGoogleCommandAtom] requires payload object')
      return
    }

    const { tokenResponse } = payload

    if (!tokenResponse || !tokenResponse.access_token) {
      console.warn('[authGoogleCommandAtom] requires tokenResponse with access_token')
      throw new Error('Token response with access_token is required')
    }

    try {
      // 调用 AuthService 进行 Google 认证
      const newAuthData = await AuthService.authGoogle(tokenResponse)

      // 更新 authDataAtom
      set(authDataAtom, newAuthData)

      return newAuthData
    } catch (error) {
      console.error('[authGoogleCommandAtom] Google authentication failed:', error)
      throw error
    }
  },
)

/**
 * 登出命令原子
 *
 * 使用方式：
 * const logout = useSetAtom(logoutCommandAtom)
 * await logout({  })
 *
 * 或者如果不需要调用后端 API：
 * await logout()
 *
 * @param {Object} [payload] - 登出参数（可选）
 *
 * 此命令会：
 * - 如果有 token 和 api，调用 AuthService.logout 通知后端
 * - 断开 WebSocket 连接
 * - 清空 authDataAtom
 */
export const logoutCommandAtom = atom(
  null, // read function (not used for commands)
  async (get, set) => {
    // payload 是可选的

    try {
      // 获取当前认证数据
      const currentAuthData = get(authDataAtom)

      // 如果有 token 和 api，调用后端登出接口
      if (currentAuthData?.token) {
        try {
          await AuthService.logout()
        } catch (error) {
          // 即使后端登出失败，也继续执行本地清理
          console.error('[logoutCommandAtom] logout API call failed:', error)
        }
      }

      // 断开 WebSocket 连接
      ChatService.disconnect()

      // 清空认证数据
      set(authDataAtom, null)
    } catch (error) {
      console.error('[logoutCommandAtom] logout failed:', error)
      // 即使出错，也尝试清空本地状态
      ChatService.disconnect()
      set(authDataAtom, null)
      throw error
    }
  },
)

/**
 * 清理认证数据命令原子
 *
 * 使用方式：
 * const cleanup = useSetAtom(authCleanupCommandAtom)
 * await cleanup()
 *
 * 此命令会：
 * - 断开 WebSocket 连接
 * - 清空 authDataAtom
 */
export const authCleanupCommandAtom = atom(null, (get, set) => {
  ChatService.disconnect()
  set(authDataAtom, null)
})
